import {useState} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import DateDisplay from "components/datetime/DateDisplay";
import {ContainerSVG} from "components/waste-icons/svgs/ContainerSVG";
import {IconFillingColoured} from "site-apps/assets/AssetColorsIcons";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

const FleetRouteIslandCard = ({planned, executed, wasteType, highlighted, onToggleHighlight}) => {
    const {t: _Tf} = useTranslation("forms");
    const {t: _CT} = useCustomTranslation(translations);
    const fillingValue = planned && (planned.filling != null ? planned.filling : planned.island_filling);

    const name = planned?.uid || planned?.island_name || planned?.waypoint_name || planned?.name || "-";
    const address = planned?.full_address || planned?.island_address || "";
    const observations = planned?.observations || null;

    // UIDs de los contenedores de la parada: en una isla llegan varios en
    // `uids`, en un contenedor suelto uno solo en `uid`.
    const uidList = (() => {
        const raw = planned?.uids ? planned.uids : planned?.is_asset ? planned?.uid : "";
        const list = String(raw || "")
            .split(",")
            .map((u) => u.trim())
            .filter(Boolean);
        return list.filter((u, i) => list.indexOf(u) === i);
    })();

    // Resultado por contenedor de la sesión: "ok" recogido, "ko" no recogido.
    const assetResults = {};
    (executed?.assets || []).forEach((a) => {
        if (a?.uid) assetResults[a.uid] = a.result;
    });

    // Coordenadas solo para el fantasma de incidencia del conductor (type "s"):
    // sirven para localizar el aviso en un mapa externo o en BD. En islas y
    // waypoints normales el operario no las necesita.
    const showCoords = planned?.waypoint_type === "s";
    const coords =
        showCoords && planned?.lat != null && planned?.lng != null
            ? `${Number(planned.lat).toFixed(6)}, ${Number(planned.lng).toFixed(6)}`
            : null;
    const [copied, setCopied] = useState(false);

    const copyCoords = async () => {
        if (!coords) return;
        try {
            await navigator.clipboard.writeText(coords);
        } catch (e) {
            // el panel puede servirse por http (sin contexto seguro): sin Clipboard API
            const ta = document.createElement("textarea");
            ta.value = coords;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
            } finally {
                document.body.removeChild(ta);
            }
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const isNo = executed?.answer === "no";
    const reasonText =
        executed?.reason?.customText ||
        executed?.reason?.note ||
        executed?.reason?.reasonLabel ||
        executed?.reason?.reason ||
        null;
    const dirt = executed?.client_payload?.dirt;

    let dirtLabel = null;
    switch (dirt) {
        case "clean":
            dirtLabel = _Tf("labels.dirt-clean");
            break;
        case "normal":
            dirtLabel = _Tf("labels.dirt-normal");
            break;
        case "dirty":
            dirtLabel = _Tf("labels.dirt-dirty");
            break;
        default:
            dirtLabel = null;
    }

    // ojo a la derecha de la línea de estado
    const eyeButton = onToggleHighlight && (
        <button
            type="button"
            className={`btn btn-link p-0 ms-auto ${highlighted ? "text-primary" : "text-body-tertiary"}`}
            title={_CT("show-on-map")}
            onClick={onToggleHighlight}>
            <FontAwesomeIcon icon="eye" size="lg" />
        </button>
    );

    return (
        <div className="card-asset-basic p-2">
            <div className="row g-0 align-items-center">
                <div className="col-auto text-center pe-2" style={{width: "48px", minWidth: "48px"}}>
                    {planned?.is_asset || planned?.is_island ? (
                        <ContainerSVG type={planned?.waste_type || wasteType} />
                    ) : (
                        <FontAwesomeIcon
                            icon={planned?.waypoint_icon || "location-dot"}
                            className={planned?.waypoint_type === "s" ? "text-warning" : "text-secondary"}
                        />
                    )}
                    <div>
                        <span className="h4 text-secondary mb-0">{planned?.order ?? "-"}</span>
                    </div>
                </div>
                <div className="col ps-1">
                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-1">
                        <div>
                            <div className="fw-semibold">{name}</div>
                            {address ? <div className="small text-muted">{address}</div> : null}
                            {uidList.length ? (
                                <div className="small text-muted">
                                    <strong>{_CT("uids")}:</strong>{" "}
                                    {uidList.map((uid) => {
                                        const result = assetResults[uid];
                                        return (
                                            <span key={uid} className="me-2 font-monospace text-nowrap">
                                                {uid}
                                                {result === "ok" ? (
                                                    <FontAwesomeIcon
                                                        icon="check"
                                                        className="text-success ms-1"
                                                    />
                                                ) : result === "ko" ? (
                                                    <FontAwesomeIcon
                                                        icon="xmark"
                                                        className="text-danger ms-1"
                                                    />
                                                ) : null}
                                            </span>
                                        );
                                    })}
                                </div>
                            ) : null}
                            {observations ? (
                                <div className="small text-muted">
                                    <strong>{_CT("observations")}:</strong> {observations}
                                </div>
                            ) : null}
                            {coords ? (
                                <div className="d-flex align-items-center gap-1 small text-muted">
                                    <span>{_CT("coordinates")}:</span>
                                    <span className="font-monospace user-select-all">{coords}</span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        title={copied ? _CT("copied") : _CT("copy-coordinates")}
                                        onClick={copyCoords}>
                                        <FontAwesomeIcon
                                            icon={copied ? "check" : "clone"}
                                            className={copied ? "text-success" : "text-body-tertiary"}
                                        />
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className="d-flex flex-column align-items-end mt-1">
                        {planned?.is_asset && fillingValue != null ? (
                            <span className="mb-1">
                                <IconFillingColoured value={fillingValue} />
                            </span>
                        ) : null}
                        {executed?.weight != null ? (
                            <span>
                                <span className="me-2">{executed.weight} kg</span>
                                <FontAwesomeIcon icon="weight-hanging" className="text-secondary" />
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="mt-2 pt-2 small" style={{borderTop: "1px dashed #dee2e6"}}>
                {executed ? (
                    <>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="fw-semibold">
                                {_Tf("labels.operative-completed")}:
                                {isNo ? (
                                    <FontAwesomeIcon icon="xmark" className="text-danger ms-1" />
                                ) : (
                                    <FontAwesomeIcon icon="check" className="text-success ms-1" />
                                )}
                            </span>
                            {eyeButton}
                        </div>
                        {reasonText && (
                            <div className="mt-1 text-muted">
                                <strong>{_Tf(isNo ? "labels.reason" : "labels.observations")}:</strong> {reasonText}
                            </div>
                        )}

                        {dirtLabel ? (
                            <div className="mt-1 text-muted">
                                <strong>{_Tf("labels.dirt")}:</strong> {dirtLabel}
                            </div>
                        ) : null}
                        <div className="mt-1 text-muted">
                            <strong>{_Tf("labels.arrived")}:</strong>{" "}
                            <DateDisplay date={executed.arrived_at} format="DD/MM/YYYY HH:mm" />
                        </div>
                    </>
                ) : (
                    <div className="d-flex align-items-center">
                        <span className="text-muted">{_Tf("labels.not-registered")}</span>
                        {eyeButton}
                    </div>
                )}
            </div>
        </div>
    );
};
export default FleetRouteIslandCard;
